/**
 * Opportunity Calculator v2.3 — shared question bank.
 *
 * Canonical scoring inputs for the AIP calculator and the mi6group.com
 * marketing-site calculator. Both trees vendor an identical copy of this
 * file under shared/calculator-questions.ts so the math, the section
 * structure, and the question text never drift.
 *
 * v2.3 change (2026-05-07): Bryan locked section count at 10 — added
 * Risk + Implementation as section 10. Question count raised to 22.
 * Quick Score raised to 7 (adds R1 from Risk + Implementation).
 *
 * v2.2 change (2026-05-07): Bryan locked question count at 20. Questions
 * are GO/NO-GO gate-check questions only — the kind where a hard "no" or
 * "absent" answer makes the deal genuinely unworkable. Wording is Bryan's
 * to tweak; the structural distribution is locked at 22 across 10 sections.
 *
 * Scoring model:
 *   5 — Strong yes / confirmed / fully in place    → 4 points
 *   4 — Mostly yes / likely / mostly in place      → 3 points
 *   3 — Partial / mixed signals / unclear          → 2 points
 *   2 — Mostly no / weak / mostly missing          → 1 point
 *   1 — Hard no / blocked / absent                 → 0 points
 *   Skip                                           → null (excluded from denominator)
 *
 * composite_0_100 = round( (sum_points / (4 × count_answered)) × 100 )
 * Confidence: high = ≥80% answered, medium = 50–79%, low = <50%
 * RAG: 70+ green, 50–69 amber, <50 red
 *
 * Manufacturing-only positioning. M-PACT with hyphen. No emojis.
 *
 *   FULL  — 22 questions across 10 sections, gate-check gates.
 *   QUICK — 7 questions, one per major gate section, ~105 seconds.
 *           Sections: Budget, Authority, Need, Timeline, Competitor, Coverage, Risk+Implementation.
 */

export type SectionId =
  | "BUDGET"
  | "AUTHORITY"
  | "NEED"
  | "TIMELINE"
  | "COMPLIANCE"
  | "FINANCIAL"
  | "COMPETITOR"
  | "ORGANIZATION"
  | "COVERAGE"
  | "RISK";

export interface Section {
  id: SectionId;
  title: string;     // Title-case display
  caption: string;   // Short kicker shown under the title
  weight: number;    // Section weight in the overall composite (sum to 100)
}

/**
 * Section list — order in which the survey is walked. Weights sum to 100.
 */
export const SECTIONS: Section[] = [
  { id: "BUDGET",       title: "Budget",                   caption: "Funded, defended, and aligned to fiscal cycle.",       weight: 15 },
  { id: "AUTHORITY",    title: "Authority",                caption: "Economic buyer named, engaged, sign-off path clear.",  weight: 15 },
  { id: "NEED",         title: "Need",                     caption: "Trigger event present, pain quantified.",              weight: 14 },
  { id: "TIMELINE",     title: "Timeline",                 caption: "Close date defensible, trigger event tied to date.",   weight: 12 },
  { id: "COMPLIANCE",   title: "Compliance + Legal",       caption: "Regulatory blockers cleared, legal path mapped.",      weight: 8  },
  { id: "FINANCIAL",    title: "Financial",                caption: "Buyer financially sound, not a tire-kicker.",          weight: 10 },
  { id: "COMPETITOR",   title: "Competitor",               caption: "Incumbent identified, our wedge is clear.",            weight: 8  },
  { id: "ORGANIZATION", title: "Organization + Readiness", caption: "Org can absorb the solution, change capacity exists.", weight: 10 },
  { id: "COVERAGE",     title: "People Network + Coverage",caption: "Champion coached, multi-threaded, blocker neutralized.",weight: 8  },
  { id: "RISK",         title: "Risk + Implementation",     caption: "Deployment risk owned, integration scoped, change-management cleared.", weight: 8  },
];

/**
 * Option value is the raw point score (0–4) for this choice.
 * composite_0_100 = round( (sum_points / (4 × count_answered)) × 100 )
 */
export interface Option {
  value: number;  // 0..4 point value
  label: string;
  anchor: string; // generic anchor label per spec
  leftBorderColor: string; // 4px left border color
}

export interface Question {
  id: string;
  section: SectionId;
  stem: string;
  /** When true, included in QUICK 6-question fast pass. */
  quick?: boolean;
}

/** Standard 5-point graded options — same for every question. */
export const GRADED_OPTIONS: Option[] = [
  { value: 4, label: "Strong yes — confirmed / fully in place",   anchor: "Strong",  leftBorderColor: "#15803D" },
  { value: 3, label: "Mostly yes — likely / mostly in place",     anchor: "Mostly",  leftBorderColor: "#22C55E" },
  { value: 2, label: "Partial — mixed signals / unclear",         anchor: "Partial", leftBorderColor: "#F59E0B" },
  { value: 1, label: "Weak — mostly missing / mostly no",         anchor: "Weak",    leftBorderColor: "#F97316" },
  { value: 0, label: "Absent — hard no / blocked",                anchor: "Absent",  leftBorderColor: "#DC2626" },
];

/**
 * QUESTIONS — 22 total, gate-check only.
 * Distribution: Budget 3, Authority 3, Need 2, Timeline 2,
 *               Compliance 2, Financial 1, Competitor 2,
 *               Organization 2, Coverage 3, Risk 2.
 * Seven flagged `quick: true` — one per major gate section
 * (Budget, Authority, Need, Timeline, Competitor, Coverage, Risk+Implementation).
 */
export const QUESTIONS: Question[] = [

  // ── 1. BUDGET (3 questions) ────────────────────────────────────────────────
  {
    id: "budget-funded",
    section: "BUDGET",
    quick: true,
    stem: "Is budget formally identified, defended by the customer, and aligned to the current fiscal year?",
  },
  {
    id: "budget-fiscal",
    section: "BUDGET",
    stem: "Is the budget released and available to spend before the target close date?",
  },
  {
    id: "budget-roi-threshold",
    section: "BUDGET",
    stem: "Has the customer stated an ROI threshold or payback ceiling that this deal must clear to get funded?",
  },

  // ── 2. AUTHORITY (3 questions) ─────────────────────────────────────────────
  {
    id: "authority-economic-buyer",
    section: "AUTHORITY",
    quick: true,
    stem: "Is the economic buyer — the operator who can authorize the spend without further approval — named and confirmed?",
  },
  {
    id: "authority-engaged",
    section: "AUTHORITY",
    stem: "Has the economic buyer been directly engaged and has shown personal interest in solving this problem?",
  },
  {
    id: "authority-signoff-path",
    section: "AUTHORITY",
    stem: "Is the full sign-off path — who approves, in what sequence, at what thresholds — mapped and confirmed?",
  },

  // ── 3. NEED (2 questions) ──────────────────────────────────────────────────
  {
    id: "need-compelling-event",
    section: "NEED",
    quick: true,
    stem: "Is there a firm or formal timeline — a production target, a line launch, a regulatory deadline, a contract expiry — that makes doing nothing costlier than acting?",
  },
  {
    id: "need-quantified-pain",
    section: "NEED",
    stem: "Has the customer quantified the cost or operational impact of the current problem in their own numbers?",
  },

  // ── 4. TIMELINE (2 questions) ──────────────────────────────────────────────
  {
    id: "timeline-close-date",
    section: "TIMELINE",
    quick: true,
    stem: "Is there a firm, customer-driven close date that you could defend to a forecasting committee?",
  },
  {
    id: "timeline-trigger",
    section: "TIMELINE",
    stem: "Is the close date tied to a specific trigger event on the customer's side — not to your quarter-end?",
  },

  // ── 5. COMPLIANCE + LEGAL (2 questions) ───────────────────────────────────
  {
    id: "compliance-regulatory",
    section: "COMPLIANCE",
    stem: "Are any regulatory or standards-compliance blockers — ITAR, ISO, OSHA, EPA, safety certs — identified and on a path to clearance?",
  },
  {
    id: "compliance-legal-engaged",
    section: "COMPLIANCE",
    stem: "Is legal and procurement on both sides engaged early enough to avoid a review-cycle that breaks the close date?",
  },

  // ── 6. FINANCIAL (1 question) ─────────────────────────────────────────────
  {
    id: "financial-health",
    section: "FINANCIAL",
    stem: "Is the buyer organization financially healthy enough to see this purchase through — not distressed, not in a freeze, not running on a continuing resolution?",
  },

  // ── 7. COMPETITOR (2 questions) ───────────────────────────────────────────
  {
    id: "competitor-incumbent",
    section: "COMPETITOR",
    quick: true,
    stem: "Is the incumbent vendor or the status quo identified, and do you have a confirmed wedge that makes the customer willing to displace it?",
  },
  {
    id: "competitor-wedge",
    section: "COMPETITOR",
    stem: "Are all active competing vendors named, and does the customer's sponsor lean toward us on at least one decisive dimension?",
  },

  // ── 8. ORGANIZATION + READINESS (2 questions) ────────────────────────────
  {
    id: "org-absorb",
    section: "ORGANIZATION",
    stem: "Does the customer's operation have the capacity to absorb and deploy this solution — people, IT infrastructure, process bandwidth — within the projected timeline?",
  },
  {
    id: "org-change-management",
    section: "ORGANIZATION",
    stem: "Is there a named change owner on the customer side with the mandate and authority to drive adoption past the implementation finish line?",
  },

  // ── 9. PEOPLE NETWORK + COVERAGE (3 questions) ───────────────────────────
  {
    id: "coverage-champion",
    section: "COVERAGE",
    quick: true,
    stem: "Is there an identified, coached champion inside the customer who is actively selling this deal internally on your behalf?",
  },
  {
    id: "coverage-multi-thread",
    section: "COVERAGE",
    stem: "Do you have active relationships with at least two independent threads inside the buying organization — so the deal does not collapse if one contact leaves or goes quiet?",
  },
  {
    id: "coverage-blocker",
    section: "COVERAGE",
    stem: "Is any known blocker — a skeptical VP, a competing internal project, a budget owner who has not bought in — identified and neutralized or actively being worked?",
  },

  // ── 10. RISK + IMPLEMENTATION (2 questions) ───────────────────────────────────────
  {
    id: "risk-technical-owner",
    section: "RISK",
    quick: true,
    stem: "Has the customer's technical team confirmed they can absorb the integration without rebuilding their existing stack — and is there a named technical owner accountable for the cutover?",
  },
  {
    id: "risk-deployment-owned",
    section: "RISK",
    stem: "Are the highest-impact deployment risks — change management, data migration, downtime windows, training capacity — identified, scoped, and owned by the customer's side, not handed back to us to figure out?",
  },
];

/** Quick Score — 7 gate questions, one per major section. */
export const QUICK_QUESTIONS: Question[] = QUESTIONS.filter((q) => q.quick);

/** Section lookup helper. */
export function sectionFor(id: SectionId): Section {
  const s = SECTIONS.find((x) => x.id === id);
  if (!s) throw new Error(`Unknown section: ${id}`);
  return s;
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

/** null = skipped (excluded from denominator) */
export type Answers = Record<string, number | null>;

export interface SectionScore {
  id: SectionId;
  title: string;
  /** 0..100 — null if zero questions answered in section */
  pct: number | null;
  /** Percentage of section questions answered (0..100) */
  coveragePct: number;
  answered: number;
  total: number;
}

export interface CalculatorResult {
  composite: number;             // 0..100
  band: "GREEN" | "AMBER" | "RED";
  verdict: "go-forward" | "work-the-gaps" | "cut-bait";
  verdictLine: string;           // plain-English go/no-go line
  confidenceTag: "high" | "medium" | "low";
  answeredCount: number;
  totalCount: number;
  sections: SectionScore[];
  strongest: SectionId[];        // top 2 by pct
  weakest: SectionId[];          // bottom 2 by pct
}

/**
 * computeScore — v2.2 composite math.
 *
 * composite_0_100 = round( (sum_points_answered / (4 × count_answered)) × 100 )
 * Skipped (null) answers excluded from numerator AND denominator.
 *
 * Section pct uses the same per-section logic.
 * Confidence: high ≥80% of total questions answered, medium 50–79%, low <50%.
 * RAG: 70+ green, 50–69 amber, <50 red.
 */
export function computeScore(
  answers: Answers,
  questions: Question[] = QUESTIONS,
): CalculatorResult {
  const totalCount = questions.length;

  // Count answered (non-null, non-undefined)
  const answeredEntries = questions.filter(
    (q) => answers[q.id] !== undefined && answers[q.id] !== null,
  );
  const answeredCount = answeredEntries.length;

  // Overall composite
  const sumPoints = answeredEntries.reduce(
    (acc, q) => acc + (answers[q.id] as number),
    0,
  );
  const composite =
    answeredCount > 0
      ? Math.round((sumPoints / (4 * answeredCount)) * 100)
      : 0;

  // Confidence
  const answeredRatio = totalCount > 0 ? answeredCount / totalCount : 0;
  const confidenceTag: CalculatorResult["confidenceTag"] =
    answeredRatio >= 0.8 ? "high" : answeredRatio >= 0.5 ? "medium" : "low";

  // Band
  const band: CalculatorResult["band"] =
    composite >= 70 ? "GREEN" : composite >= 50 ? "AMBER" : "RED";

  // Verdict
  let verdict: CalculatorResult["verdict"];
  let verdictLine: string;
  if (composite >= 70) {
    verdict = "go-forward";
    verdictLine = "Worth pursuing — strong gates clear.";
  } else if (composite >= 50) {
    verdict = "work-the-gaps";
    verdictLine = "Mixed signals — work the gaps before you go further.";
  } else {
    verdict = "cut-bait";
    verdictLine = "Cut bait — gates not viable, save the time.";
  }

  // Per-section breakdown
  const sectionsInUse = SECTIONS.filter((s) =>
    questions.some((q) => q.section === s.id),
  );

  const sectionScores: SectionScore[] = sectionsInUse.map((s) => {
    const sectionQs = questions.filter((q) => q.section === s.id);
    const answeredQs = sectionQs.filter(
      (q) => answers[q.id] !== undefined && answers[q.id] !== null,
    );
    const sectionSum = answeredQs.reduce(
      (acc, q) => acc + (answers[q.id] as number),
      0,
    );
    const pct =
      answeredQs.length > 0
        ? Math.round((sectionSum / (4 * answeredQs.length)) * 100)
        : null;
    const coveragePct = Math.round((answeredQs.length / sectionQs.length) * 100);
    return {
      id: s.id,
      title: s.title,
      pct,
      coveragePct,
      answered: answeredQs.length,
      total: sectionQs.length,
    };
  });

  // Strongest / weakest by pct (only sections with answers)
  const ranked = [...sectionScores]
    .filter((s) => s.pct !== null)
    .sort((a, b) => (b.pct as number) - (a.pct as number));
  const strongest = ranked.slice(0, 2).map((s) => s.id);
  const weakest = ranked.slice(-2).map((s) => s.id);

  return {
    composite,
    band,
    verdict,
    verdictLine,
    confidenceTag,
    answeredCount,
    totalCount,
    sections: sectionScores,
    strongest,
    weakest,
  };
}

// ─── Commentary ──────────────────────────────────────────────────────────────

export const SECTION_STRENGTH_LINES: Record<SectionId, string> = {
  BUDGET:       "Budget is named, defended, and fiscal-aligned.",
  AUTHORITY:    "The economic buyer is mapped and engaged.",
  NEED:         "A firm timeline is in place and pain is quantified.",
  TIMELINE:     "Close date is defensible and tied to a real trigger.",
  COMPLIANCE:   "Compliance and legal paths are clear and moving.",
  FINANCIAL:    "The buyer is financially stable and not a tire-kicker.",
  COMPETITOR:   "Incumbent is identified and you have a confirmed wedge.",
  ORGANIZATION: "The customer can absorb and deploy the solution.",
  COVERAGE:     "Champion is active, deal is multi-threaded, blockers worked.",
  RISK:         "Deployment risks are owned, integration is scoped, and the customer's technical team is accountable.",
};

export const SECTION_RISK_LINES: Record<SectionId, string> = {
  BUDGET:       "Budget is unconfirmed — get a written, fiscally aligned figure before commit.",
  AUTHORITY:    "Economic buyer is fuzzy — name the operator who can sign and engage them.",
  NEED:         "No firm timeline or unquantified pain — the customer can wait, which means they will.",
  TIMELINE:     "Close date is not tied to a customer trigger — it will slip.",
  COMPLIANCE:   "Compliance or legal path is unclear — engage both sides early or it breaks the date.",
  FINANCIAL:    "Buyer financial health is uncertain — qualify before investing more time.",
  COMPETITOR:   "Incumbent or competitor picture is murky — name what you are replacing and why.",
  ORGANIZATION: "Customer cannot absorb the solution yet — implementation risk is a deal risk.",
  COVERAGE:     "Coverage is single-threaded or champion is uncoached — multi-thread before this stalls.",
  RISK:         "Deployment risk is unowned on the customer side — if they cannot name a technical owner, this deal will bog down in implementation and come back to you as a problem.",
};
